import { useEffect, useRef, useState, type ReactNode } from 'react';
import { IconClose } from './icons';
import { ScanInput } from './ScanInput';
import {
  SCAN_DEBOUNCE_MS,
  createScanDebouncer,
  scanFeedback,
  type DetectorLoader,
  type ScanVerdict,
} from './scanner';

export interface ScannerResult {
  code: string;
  tone: ScanVerdict;
  message: string;
}

/** Every fixed word on the camera screen, so an app can show it in another language. */
export interface ScannerLabels {
  starting: string;
  denied: string;
  unavailable: string;
  detectorFailed: string;
  light: string;
  on: string;
  off: string;
  done: string;
  typeLabel: string;
  typePlaceholder: string;
}

export const DEFAULT_SCANNER_LABELS: ScannerLabels = {
  starting: 'Starting the camera…',
  denied:
    'The camera is blocked. Allow it for this site in the browser settings, or type the code.',
  unavailable: 'No camera is available on this phone. Type the code or use a scanner.',
  detectorFailed: 'The scanner could not start. Type the code instead.',
  light: 'Light',
  on: 'on',
  off: 'off',
  done: 'Done',
  typeLabel: 'Type a barcode',
  typePlaceholder: 'Or type a barcode, then Enter',
};

export interface ScannerViewProps {
  /** Finds barcodes in a video frame. The app picks the browser's detector or a polyfill. */
  loadDetector: DetectorLoader;
  /**
   * A code was read (the camera, or typed in the box). Return how it went so the phone can beep and
   * buzz the right way: `ok`, `warn` (accepted but look at it) or `bad` (refused).
   */
  onRead: (code: string) => ScanVerdict | void;
  onClose: () => void;
  title?: string | undefined;
  /** The running count shown big at the top. */
  count?: number | undefined;
  countLabel?: string | undefined;
  /** What the last read did, drawn over the picture. */
  lastResult?: ScannerResult | null | undefined;
  debounceMs?: number | undefined;
  /** How often a frame is checked. */
  frameIntervalMs?: number | undefined;
  /** Replaces `getUserMedia`. For tests and previews. */
  getStream?: (() => Promise<MediaStream>) | undefined;
  /** Replaces the fixed words (a translation). Anything left out stays English. */
  labels?: Partial<ScannerLabels> | undefined;
  children?: ReactNode;
}

type CameraState = 'starting' | 'scanning' | 'denied' | 'unavailable' | 'detector_failed';

type TorchTrack = MediaStreamTrack;

function defaultStream(): Promise<MediaStream> {
  const devices = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
  if (!devices || typeof devices.getUserMedia !== 'function') {
    return Promise.reject(new DOMException('No camera API.', 'NotFoundError'));
  }
  return devices.getUserMedia({
    audio: false,
    video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
  });
}

/**
 * Full-screen camera view with continuous scanning. Every read beeps and buzzes; the same code
 * inside `debounceMs` counts once. Typing a code always works too, so a dark warehouse or a
 * blocked camera never stops the work.
 */
export function ScannerView({
  loadDetector,
  onRead,
  onClose,
  title = 'Scan boxes',
  count,
  countLabel = 'scanned',
  lastResult,
  debounceMs = SCAN_DEBOUNCE_MS,
  frameIntervalMs = 120,
  getStream,
  labels: labelOverrides,
  children,
}: ScannerViewProps) {
  const labels = { ...DEFAULT_SCANNER_LABELS, ...labelOverrides };
  const video = useRef<HTMLVideoElement>(null);
  const [state, setState] = useState<CameraState>('starting');
  const [torchOn, setTorchOn] = useState<boolean | null>(null);
  const track = useRef<TorchTrack | null>(null);
  // The latest callbacks, so the camera loop is not restarted by a parent re-render.
  const latest = useRef({ onRead, getStream, loadDetector });
  useEffect(() => {
    latest.current = { onRead, getStream, loadDetector };
  });

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let stream: MediaStream | undefined;
    const debouncer = createScanDebouncer(debounceMs);
    const videoEl = video.current;

    const report = (code: string): void => {
      const verdict = latest.current.onRead(code) ?? 'ok';
      scanFeedback(verdict);
    };

    const run = async (): Promise<void> => {
      try {
        stream = await (latest.current.getStream ?? defaultStream)();
      } catch (err) {
        if (stopped) return;
        const name = err instanceof DOMException ? err.name : '';
        setState(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable');
        return;
      }
      if (stopped) {
        for (const t of stream.getTracks()) t.stop();
        return;
      }
      const el = video.current;
      if (el) {
        el.srcObject = stream;
        el.play().catch((err: unknown) => console.warn('The camera preview did not start.', err));
      }
      const first = stream.getVideoTracks?.()[0] as TorchTrack | undefined;
      track.current = first ?? null;
      if (
        first &&
        typeof first.getCapabilities === 'function' &&
        (first.getCapabilities() as { torch?: boolean }).torch
      ) {
        setTorchOn(false);
      }

      let detector;
      try {
        detector = await latest.current.loadDetector();
      } catch (err) {
        console.warn('The barcode detector could not load.', err);
        if (!stopped) setState('detector_failed');
        return;
      }
      if (stopped) return;
      setState('scanning');

      const tick = async (): Promise<void> => {
        if (stopped) return;
        const frame = video.current;
        if (frame && frame.readyState >= 2 && !document.hidden) {
          try {
            const found = await detector.detect(frame);
            if (stopped) return;
            for (const hit of found) {
              const code = hit.rawValue.trim();
              if (code !== '' && debouncer.accept(code)) report(code);
            }
          } catch (err) {
            // One bad frame must not end the session.
            console.warn('A camera frame could not be read.', err);
          }
        }
        if (!stopped) timer = setTimeout(() => void tick(), frameIntervalMs);
      };
      void tick();
    };
    void run();

    return () => {
      stopped = true;
      if (timer !== undefined) clearTimeout(timer);
      for (const t of stream?.getTracks() ?? []) t.stop();
      if (videoEl) videoEl.srcObject = null;
      track.current = null;
    };
  }, [debounceMs, frameIntervalMs]);

  const toggleTorch = (): void => {
    const next = !torchOn;
    const advanced = [{ torch: next }] as unknown as MediaTrackConstraintSet[];
    track.current
      ?.applyConstraints({ advanced })
      .then(() => setTorchOn(next))
      .catch((err: unknown) => console.warn('The torch could not be switched.', err));
  };

  const trouble =
    state === 'starting' || state === 'scanning'
      ? null
      : state === 'denied'
        ? labels.denied
        : state === 'unavailable'
          ? labels.unavailable
          : labels.detectorFailed;

  return (
    <div className="eb-scanner" role="dialog" aria-modal="true" aria-label={title}>
      <video ref={video} className="eb-scanner__video" playsInline muted autoPlay />
      <div className="eb-scanner__frame" aria-hidden="true" />
      <header className="eb-scanner__top">
        <div>
          <div className="eb-scanner__title">{title}</div>
          {count !== undefined ? (
            <div className="eb-scanner__count" aria-live="polite">
              <strong>{count}</strong> {countLabel}
            </div>
          ) : null}
        </div>
        <div className="eb-scanner__tools">
          {torchOn !== null ? (
            <button
              type="button"
              className="eb-scanner__btn"
              aria-pressed={torchOn}
              onClick={toggleTorch}
            >
              {labels.light} {torchOn ? labels.on : labels.off}
            </button>
          ) : null}
          <button type="button" className="eb-scanner__btn" onClick={onClose}>
            <IconClose size={22} />
            {labels.done}
          </button>
        </div>
      </header>
      {state === 'starting' ? <p className="eb-scanner__note">{labels.starting}</p> : null}
      {trouble ? (
        <p className="eb-scanner__note" role="alert">
          {trouble}
        </p>
      ) : null}
      <footer className="eb-scanner__bottom">
        {lastResult ? (
          <div className="eb-scanner__result" data-tone={lastResult.tone} role="status">
            <span className="eb-scanner__code">{lastResult.code}</span>
            <span>{lastResult.message}</span>
          </div>
        ) : null}
        {children}
        <ScanInput
          onScan={(code) => {
            const verdict = onRead(code) ?? 'ok';
            scanFeedback(verdict);
          }}
          autoFocus={false}
          keepFocus={false}
          label={labels.typeLabel}
          placeholder={labels.typePlaceholder}
        />
      </footer>
    </div>
  );
}
